import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { leistungen } from '../../../mocks/leistungen';

interface LeistungenDetailProps {
  activeId: string;
  onVisible: (id: string) => void;
}

export default function LeistungenDetail({ activeId, onVisible }: LeistungenDetailProps) {
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    leistungen.forEach((l) => {
      const el = sectionRefs.current[l.id];
      if (!el) return;

      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) onVisible(l.id);
        },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [onVisible]);

  useEffect(() => {
    const el = sectionRefs.current[activeId];
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, [activeId]);

  return (
    <div className="bg-white">
      {leistungen.map((leistung, idx) => {
        const isEven = idx % 2 === 0;
        return (
          <section
            key={leistung.id}
            id={leistung.id}
            ref={(el) => { sectionRefs.current[leistung.id] = el; }}
            className={`py-16 sm:py-24 ${isEven ? 'bg-white' : 'bg-[#F8F7F4]'}`}
          >
            <div className="container mx-auto px-4 sm:px-6">
              <div
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 lg:gap-16 items-center`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2 flex-shrink-0">
                  <div className="relative rounded-2xl overflow-hidden h-72 sm:h-96">
                    <img
                      src={leistung.image}
                      alt={leistung.imageAlt}
                      className="w-full h-full object-cover object-top"
                    />
                    {/* Icon badge */}
                    <div className="absolute top-5 left-5">
                      <div className="w-14 h-14 flex items-center justify-center bg-[#0D2B4E] rounded-xl">
                        <i className={`${leistung.icon} text-2xl text-white`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <span className="text-xs font-semibold tracking-widest text-[#0D2B4E]/60 uppercase mb-3 block">
                    Leistung {String(idx + 1).padStart(2, '0')}
                  </span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                    {leistung.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-7 text-base sm:text-lg">
                    {leistung.fullDesc}
                  </p>

                  {/* Sub-services */}
                  <div className="mb-8">
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                      Was wir anbieten
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {leistung.subLeistungen.map((sub) => (
                        <div key={sub} className="flex items-start gap-2.5 text-sm text-gray-700">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-fill text-[#0D2B4E]" />
                          </div>
                          {sub}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Advantages */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {leistung.vorteile.map((v) => (
                      <div
                        key={v.title}
                        className={`p-4 rounded-xl ${isEven ? 'bg-[#F8F7F4]' : 'bg-white'}`}
                      >
                        <div className="flex items-center gap-2.5 mb-1.5">
                          <div className="w-8 h-8 flex items-center justify-center bg-[#0D2B4E]/10 rounded-lg">
                            <i className={`${v.icon} text-[#0D2B4E]`} />
                          </div>
                          <span className="font-semibold text-sm text-gray-900">{v.title}</span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{v.text}</p>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 bg-[#0D2B4E] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0a2240] transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-send-line" />
                    </div>
                    Jetzt {leistung.title} anfragen
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
